import { Component, StrictMode, type ErrorInfo, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import "./styles.css";

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element is missing.");
}

const renderBootFailure = (title: string, message: string) => {
  rootElement.innerHTML = bootFailureHtml(title, message);
};

window.addEventListener("error", (event) => {
  renderBootFailure("Office 前端运行错误", event.message || "未知运行错误");
});

window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason instanceof Error ? event.reason.message : String(event.reason || "未知异步错误");
  renderBootFailure("Office 前端异步错误", reason);
});

window.setTimeout(() => {
  if (!rootElement.textContent?.trim() && !rootElement.querySelector("canvas")) {
    renderBootFailure("Office 启动超时", "页面根节点为空。请确认服务器已部署 202605181620 之后的构建。");
  }
}, 2500);

import("./App")
  .then(({ App }) => {
    createRoot(rootElement).render(
      <StrictMode>
        <RenderBoundary>
          <App />
        </RenderBoundary>
      </StrictMode>
    );
  })
  .catch((error: unknown) => {
    const message = error instanceof Error ? error.message : String(error || "未知错误");
    renderBootFailure("Office 前端启动失败", message);
  });

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    const map: Record<string, string> = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;"
    };
    return map[char] ?? char;
  });
}

function bootFailureHtml(title: string, message: string): string {
  return `
    <main style="min-height:100vh;display:grid;place-items:center;padding:24px;background:radial-gradient(circle at 12% 18%,rgba(45,158,175,.18),transparent 55%),radial-gradient(circle at 86% 84%,rgba(244,162,97,.16),transparent 55%),#edf3ef;font-family:Inter,'Noto Sans SC','Microsoft YaHei',system-ui,sans-serif;">
      <section style="width:min(480px,100%);display:grid;gap:16px;padding:32px;border-radius:16px;background:rgba(255,255,255,.86);box-shadow:0 28px 80px rgba(26,45,48,.16);border-top:5px solid #b54848;">
        <div style="display:flex;justify-content:space-between;gap:12px;color:#53646a;font-size:12px;text-transform:uppercase;letter-spacing:.08em;">
          <span>AeralisOffice</span>
          <small>Boot Guard</small>
        </div>
        <strong style="color:#172027;font-size:25px;line-height:1.2;">${escapeHtml(title)}</strong>
        <p style="margin:0;color:#53646a;line-height:1.7;word-break:break-word;">${escapeHtml(message)}</p>
        <a style="width:fit-content;min-height:40px;display:inline-flex;align-items:center;padding:0 18px;border-radius:10px;color:#fff;background:linear-gradient(135deg,#17323a,#2a9d8f);font-weight:800;text-decoration:none;" href="/auth/passport/start">重新登录</a>
      </section>
    </main>
  `;
}

class RenderBoundary extends Component<{ children: ReactNode }, { error: string | null }> {
  state = { error: null };

  static getDerivedStateFromError(error: unknown): { error: string } {
    return { error: error instanceof Error ? error.message : String(error || "未知错误") };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error("[AeralisOffice] render failed", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <main className="login-shell">
          <section className="login-panel">
            <div className="login-brand-line">
              <span>AeralisOffice</span>
              <small>渲染保护</small>
            </div>
            <strong>Office 界面渲染失败</strong>
            <p>{this.state.error}</p>
            <a href="/auth/passport/start">重新登录</a>
          </section>
        </main>
      );
    }
    return this.props.children;
  }
}
